import React, { useState } from "react";
import {
  Box,
  Typography,
  Divider,
  Grid,
  Paper,
  Chip,
  styled,
  Alert,
  IconButton,
} from "@mui/material";
import { Block, Cancel, Schedule, Warning } from "@mui/icons-material";
import { Visibility } from "@mui/icons-material"; // Cambiado de @material-ui/icons a @mui/icons-material
import FileUploadEnvasado from "../../fileUpload/FileUploadEnvasado";

  const formatNumber = (value) => {
    if (!value && value !== 0) return '';
    
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(numValue)) return '';
    
    const [entero, decimal] = numValue.toFixed(2).split('.');
    const enteroFormateado = entero.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    
    return `${enteroFormateado},${decimal}`;
  };
// Styled Components más compactos
const CompactPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(1.5),
  borderRadius: 8,
  boxShadow: "0 1px 8px rgba(0,0,0,0.06)",
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
}));

const DetailItem = ({ label, value, highlight }) => (
  <Grid item xs={12} sm={6}>
    <Typography
      variant="body2"
      sx={{
        display: "flex",
        alignItems: "flex-start",
        mb: 1,
        lineHeight: 1.3,
        fontSize: "0.8rem",
      }}
    >
      <Box
        component="span"
        sx={{
          fontWeight: "600",
          color: "text.secondary",
          minWidth: "100px",
          flexShrink: 0,
          fontSize: "0.75rem",
          textTransform: "uppercase",
          letterSpacing: "0.3px",
        }}
      >
        {label}:
      </Box>
      <Box
        component="span"
        sx={{
          color: highlight ? "primary.main" : "text.primary",
          fontWeight: highlight ? "600" : "400",
          wordBreak: "break-word",
          backgroundColor: highlight ? "primary.50" : "transparent",
          padding: highlight ? "1px 4px" : 0,
          borderRadius: highlight ? 0.5 : 0,
          fontSize: "0.8rem",
          ml: 0.5,
        }}
      >
        {value || "—"}
      </Box>
    </Typography>
  </Grid>
);

const ProductList = ({ products }) => (
  <Box
    sx={{
      backgroundColor: "grey.50",
      borderRadius: 1,
      p: 1,
      mt: 0.5,
      border: "1px solid",
      borderColor: "divider",
    }}
  >
    {products.map((product, i) => (
      <Typography
        key={i}
        variant="body2"
        sx={{
          color: "text.primary",
          display: "flex",
          alignItems: "flex-start",
          mb: i < products.length - 1 ? 0.5 : 0,
          padding: "2px 0",
          fontSize: "0.78rem",
          lineHeight: 1.2,
        }}
      >
        <Box
          component="span"
          sx={{
            mr: 1,
            color: "primary.main",
            fontWeight: "bold",
            fontSize: "0.8rem",
          }}
        >
          •
        </Box>
        {product}
      </Typography>
    ))}
  </Box>
);

// MODIFICADO: Recibe props adicionales
const PaymentList = ({ payments, row, onOpenFileViewer }) => {
  if (!payments) return null;

  const paymentMethods = payments
    .split("--")
    .map((p) => p.trim())
    .filter((p) => p);

  if (paymentMethods.length === 0) return null;

  // Separar pagos normales de envases
  const normalPayments = [];
  const envasePayments = [];
  let totalNormal = 0;
  let totalEnvase = 0;

  paymentMethods.forEach((payment) => {
    // Detectar si es envase
    const isEnvase = payment.toLowerCase().includes("envase");

    const parsePayment = (paymentString) => {
      // 1. Remover el #id al final (ej: #363, #369)
      const withoutId = paymentString.replace(/\s*#\d+\s*$/, "").trim();

      // 2. Buscar el monto (número con decimales, ej: 50.00, 100.00)
      const montoMatch = withoutId.match(/(\d+\.\d{2})(?=\s|$)/);

      if (montoMatch) {
        const monto = parseFloat(montoMatch[1]);
        const montoIndex = withoutId.indexOf(montoMatch[1]);

        // Método: todo antes del monto
        const metodo = withoutId.substring(0, montoIndex).trim();

        // Detalle: todo después del monto (puede estar vacío)
        const detalle = withoutId
          .substring(montoIndex + montoMatch[1].length)
          .trim();

        return {
          method: metodo,
          amount: `$${formatNumber(monto)}`,
          amountValue: monto,
          detail: detalle,
          isEnvase: isEnvase,
        };
      }

      // Si no encuentra monto con formato decimal, buscar cualquier número
      const anyNumberMatch = withoutId.match(/(\d+(?:\.\d+)?)(?=\s|$)/);
      if (anyNumberMatch) {
        const monto = parseFloat(anyNumberMatch[1]);
        const montoIndex = withoutId.indexOf(anyNumberMatch[1]);

        const metodo = withoutId.substring(0, montoIndex).trim();
        const detalle = withoutId
          .substring(montoIndex + anyNumberMatch[1].length)
          .trim();

        return {
          method: metodo,
          amount: `$${monto.toFixed(2)}`,
          amountValue: monto,
          detail: detalle,
          isEnvase: isEnvase,
        };
      }

      // Fallback
      return {
        method: withoutId,
        amount: "",
        amountValue: 0,
        detail: "",
        isEnvase: isEnvase,
      };
    };

    const parsed = parsePayment(payment);

    if (isEnvase) {
      envasePayments.push(parsed);
      totalEnvase += parsed.amountValue;
    } else {
      normalPayments.push(parsed);
      totalNormal += parsed.amountValue;
    }
  });

  const getPaymentColor = (method) => {
    const methodColors = {
      Efectivo: "success",
      QR: "primary",
      Tarjeta: "secondary",
      Transferencia: "info",
      Débito: "warning",
      Crédito: "error",
    };

    const methodLower = method.toLowerCase();
    for (const [key, color] of Object.entries(methodColors)) {
      if (methodLower.includes(key.toLowerCase())) {
        return color;
      }
    }
    return "default";
  };

  // MODIFICADO: Recibe onOpenFileViewer y row como props
  const PaymentCard = ({ title, payments, total, color = "success" }) => (
    <Box
      sx={{
        backgroundColor: `${color}.50`,
        borderRadius: 1,
        p: 1,
        border: "1px solid",
        borderColor: `${color}.100`,
        mb: 2,
      }}
    >
      <Typography
        variant="subtitle2"
        sx={{
          mb: 1,
          color: `${color}.main`,
          fontWeight: "600",
          fontSize: "0.8rem",
          display: "flex",
          alignItems: "center",
        }}
      >
        {title}
      </Typography>

      <Grid container spacing={0.5}>
        {payments.map((payment, index) => {
          const colorChip = getPaymentColor(payment.method);
          const isTransferencia = payment.method.toLowerCase().includes('transferencia');
          
          return (
            <Grid item xs={12} sm={6} key={index}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 0.5,
                  p: 0.75,
                  backgroundColor: "white",
                  borderRadius: 0.5,
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    flex: 1,
                  }}
                >
                  <Chip
                    label={payment.method}
                    size="small"
                    color={colorChip}
                    sx={{
                      height: 20,
                      fontSize: "0.75rem",
                      fontWeight: "600",
                    }}
                  />
                  {payment.detail && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: "text.secondary",
                        fontSize: "0.75rem",
                        ml: 0.5,
                      }}
                    >
                      {payment.detail}
                    </Typography>
                  )}
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: "600",
                      color: `${color}.700`,
                      fontSize: "0.78rem",
                      minWidth: "60px",
                      textAlign: "right",
                    }}
                  >
                    {payment.amount}
                  </Typography>
                  
                  {/* Icono de ojo solo para Transferencia */}
                  {isTransferencia && (
                    <IconButton
                      size="small"
                      onClick={() => onOpenFileViewer(`payment-${row?.id}-${index}`)}
                      sx={{
                        p: 0.5,
                        color: 'primary.main',
                        '&:hover': {
                          backgroundColor: 'primary.50',
                        },
                      }}
                    >
                      <Visibility sx={{ fontSize: '1rem' }} />
                    </IconButton>
                  )}
                </Box>
              </Box>
            </Grid>
          );
        })}
      </Grid>

      {payments.length > 1 && (
        <Box
          sx={{
            mt: 1,
            pt: 1,
            borderTop: "1px solid",
            borderColor: "divider",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: "700",
              color: `${color}.800`,
              fontSize: "0.8rem",
            }}
          >
            TOTAL:
          </Typography>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: "700",
              color: `${color}.800`,
              fontSize: "0.9rem",
            }}
          >
             ${formatNumber(total)}
          </Typography>
        </Box>
      )}
    </Box>
  );

  return (
    <Grid item xs={12}>
      <Box
        sx={{
          mt: 1.5,
          pt: 1.5,
          borderTop: "1px dashed",
          borderColor: "divider",
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            mb: 1,
            color: "success.main",
            fontWeight: "600",
            fontSize: "0.8rem",
            display: "flex",
            alignItems: "center",
          }}
        >
          💳 Medios de Pago
        </Typography>

        {/* Pagos Normales */}
        {normalPayments.length > 0 && (
          <PaymentCard
            title="Cargas"
            payments={normalPayments}
            total={totalNormal}
            color="success"
          />
        )}

        {/* Pagos de Envases */}
        {envasePayments.length > 0 && (
          <PaymentCard
            title="Envases"
            payments={envasePayments}
            total={totalEnvase}
            color="secondary"
          />
        )}

        {/* Total General si hay ambos tipos */}
        {normalPayments.length > 0 && envasePayments.length > 0 && (
          <Box
            sx={{
              mt: 1,
              pt: 1,
              borderTop: "1px solid",
              borderColor: "divider",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              backgroundColor: "primary.50",
              borderRadius: 1,
              p: 1.5,
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: "700",
                color: "primary.800",
                fontSize: "0.9rem",
              }}
            >
              TOTAL GENERAL:
            </Typography>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: "700",
                color: "primary.800",
                fontSize: "1rem",
              }}
            >
              ${formatNumber(totalNormal + totalEnvase)}
            </Typography>
          </Box>
        )}
      </Box>
    </Grid>
  );
};

const StatusChip = ({ status }) => {
  const getStatusColor = (status) => {
    const statusMap = {
      Rechazado: "error",
      "Factura Anulada": "error",
      Completado: "success",
      Pendiente: "warning",
      "En Proceso": "info",
    };
    return statusMap[status] || "default";
  };

  return (
    <Chip
      label={status}
      size="small"
      color={getStatusColor(status)}
      sx={{
        fontSize: "0.65rem",
        height: 20,
        fontWeight: "600",
      }}
    />
  );
};

const observacionesConfig = {
  reclamo: {
    icon: Warning,
    title: "Reclamo del pedido",
    color: "error",
    bgcolor: "error.light",
    field: "obs_reclamo",
  },
  rechazo: {
    icon: Cancel,
    title: "Motivo de rechazo",
    color: "error",
    bgcolor: "error.light",
    field: "desc_motivo_rechazo",
  },
  pospuesto: {
    icon: Schedule,
    title: "Motivo de posposición",
    color: "warning",
    bgcolor: "warning.light",
    field: "obs_pospuesto",
  },
  anulado: {
    icon: Block,
    title: "Motivo de anulación",
    color: "error",
    bgcolor: "error.light",
    field: "obs_anulado",
  },
};

const renderObservacionChofer = (tipo, row) => {
  const config = observacionesConfig[tipo];
  const valor = row[config.field];

  if (!valor) return null;

  const IconComponent = config.icon;

  return (
    <Alert
      severity={config.color}
      icon={<IconComponent />}
      sx={{
        mb: 1,
        "& .MuiAlert-message": {
          width: "100%",
        },
      }}
    >
      <Typography variant="subtitle2" fontWeight="600" gutterBottom>
        {config.title}
      </Typography>
      <Typography variant="body2">{valor}</Typography>
    </Alert>
  );
};

const DetallePedidos = ({ row, hoja_ruta }) => {
  const [fileViewerOpen, setFileViewerOpen] = useState(false);
  const [selectedPaymentId, setSelectedPaymentId] = useState(null);

  const handleOpenFileViewer = (paymentId) => {
    setSelectedPaymentId(row.uuid_pedido);
    setFileViewerOpen(true);
  };  

  const handleCloseFileViewer = () => {
    setFileViewerOpen(false);
    setSelectedPaymentId(null);
  };
  
  const renderOrderDetails = () => (
    <>
      <DetailItem label="N° Pedido" value={row.id} highlight />
      <DetailItem label="Cliente" value={row.cliente} />
      <DetailItem label="Dirección" value={row.direccion_entrega} />
      <DetailItem label="Fecha" value={row.fecha_para} />

      <Grid item xs={12} sm={6}>
        <Typography
          variant="body2"
          sx={{
            display: "flex",
            alignItems: "center",
            mb: 1,
            lineHeight: 1.3,
            fontSize: "0.8rem",
          }}
        >
          <Box
            component="span"
            sx={{
              fontWeight: "600",
              color: "text.secondary",
              minWidth: "100px",
              flexShrink: 0,
              fontSize: "0.75rem",
              textTransform: "uppercase",
              letterSpacing: "0.3px",
            }}
          >
            Estado:
          </Box>
          <StatusChip status={row.valor} />
        </Typography>
      </Grid>

      <DetailItem label="Sucursal" value={row.sucursal_cliente} />
      <DetailItem label="Chofer" value={row.nombre_apellido} />
      {row.zona && <DetailItem label="Zona" value={row.zona} />}
      {row.remito_envase && (
        <DetailItem label="Remito Envase" value={row.remito_envase} />
      )}
      {row.recibo_envase && (
        <DetailItem label="Recibo Envase" value={row.recibo_envase} />
      )}

      {row.remito_entrega && (
        <DetailItem label="Remito de Entrega" value={row.remito_entrega} />
      )}

      <Grid item xs={12} sx={{ mt: 0.5 }}>
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: "600",
            color: "text.secondary",
            mb: 0.5,
            fontSize: "0.8rem",
            textTransform: "uppercase",
            letterSpacing: "0.3px",
          }}
        >
          📦 Productos
        </Typography>
        {row.producto ? (
          <ProductList
            products={row.producto.split("--").map((p) => p.trim())}
          />
        ) : (
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              fontStyle: "italic",
              fontSize: "0.8rem",
            }}
          >
            Sin productos
          </Typography>
        )}
        {(row.obs_reclamo ||
          row.desc_motivo_rechazo ||
          row.obs_pospuesto ||
          row.obs_anulado) && (
          <Box>
            {renderObservacionChofer("reclamo", row)}
            {renderObservacionChofer("rechazo", row)}
            {renderObservacionChofer("pospuesto", row)}
            {renderObservacionChofer("anulado", row)}
          </Box>
        )}
      </Grid>
    </>
  );

  const renderObservations = () => (
    <Grid item xs={12}>
      <Typography
        variant="subtitle2"
        sx={{
          fontWeight: "600",
          color: "warning.main",
          mb: 0.5,
          fontSize: "0.8rem",
          textTransform: "uppercase",
          letterSpacing: "0.3px",
        }}
      >
        📝 Observaciones
      </Typography>
      <Box
        sx={{
          backgroundColor: "warning.50",
          borderRadius: 1,
          p: 1,
          border: "1px solid",
          borderColor: "warning.100",
          color: "text.primary",
          whiteSpace: "pre-wrap",
          fontSize: "0.8rem",
          lineHeight: 1.3,
        }}
      >
        {row.desc_motivo_nocompra || row.observaciones || "Sin observaciones"}
      </Box>
    </Grid>
  );

  return (
    <CompactPaper>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1.5,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            color: "primary.main",
            fontWeight: "700",
            fontSize: "1rem",
            display: "flex",
            alignItems: "center",
            gap: 0.5,
          }}
        >
          📋 Detalles del Pedido {row.pedido_nuevo === 1 || row.fecha_asignado === null? "- (Desde Pedido Nuevo)":""} 
        </Typography>
        {row.a_consfinal === 1 ? (
          <Chip
            label="A Consumidor Final"
            size="small"
            sx={{
              backgroundColor: "#E53935",
              color: "#fff",
              fontSize: "0.65rem",
              height: 20,
              fontWeight: "600",
            }}
          />
        ) : null}
        {row.pedido_cambio === 1 ? (
          <>
          <Chip
            label="Cambio"
            size="small"
            sx={{
              backgroundColor: "#E53935",
              color: "#fff",
              fontSize: "0.65rem",
              height: 20,
              fontWeight: "600",
            }}
          />
          <Typography
            variant="subtitle2"
          sx={{
            // fontWeight: "600",
            color: "text.secondary",
            // mb: 0.5,
            fontSize: "0.8rem",
            textTransform: "uppercase",
            letterSpacing: "0.3px",
          }}
          > N° Pegatina: <strong>{row.nro_pegatina_cambio}</strong></Typography>
          </>
        ) : null}

        <StatusChip status={row.valor} />
      </Box>

      <Divider sx={{ mb: 1.5, borderColor: "divider" }} />

      <Grid container spacing={1}>
        {row.origen === "Telefonista"
          ? renderOrderDetails()
          : row.vendio === 1
            ? renderOrderDetails()
            : renderObservations()}

        {/* Sección de Pagos - MODIFICADO: pasamos row y handleOpenFileViewer */}
        {row.pago && (
          <PaymentList 
            payments={row.pago} 
            row={row} 
            onOpenFileViewer={handleOpenFileViewer} 
          />
        )}
        {row.medios_pago_combinados && (
          <PaymentList 
            payments={row.medios_pago_combinados} 
            row={row} 
            onOpenFileViewer={handleOpenFileViewer} 
          />
        )}
      </Grid>

      {/* Componente FileUploadEnvasado - MOVIDO fuera del PaymentList */}
      <FileUploadEnvasado
        open={fileViewerOpen}
        fileId={selectedPaymentId}
        onClose={handleCloseFileViewer}
        canEdit={false}
        title="Comprobantes de Transferencia"
      />
    </CompactPaper>
  );
};

export default DetallePedidos;